import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  AvailabilityOwnerType,
  LegacyAvailabilityMigrationResult,
} from 'src/app/constants/availability.types';

import { AvailabilityService } from 'src/app/services/availability.service';
import { CommanUsesService } from 'src/app/services/comman-uses.service';

@Component({
  selector: 'app-availability-migration-card',
  templateUrl: './availability-migration-card.component.html',
  styleUrls: ['./availability-migration-card.component.scss'],
})
export class AvailabilityMigrationCardComponent implements OnChanges {
  @Input() ownerId!: string | number;
  @Input() ownerType!: AvailabilityOwnerType;

  @Output() migrationCompleted = new EventEmitter<void>();

  loading = false;
  migrating = false;

  previewCompleted = false;
  completed = false;

  result: LegacyAvailabilityMigrationResult | null = null;
  errorMessage = '';

  constructor(
    private readonly availabilityService: AvailabilityService,
    private readonly commanUsesService: CommanUsesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ownerId || changes.ownerType) {
      this.reset();
    }
  }

  async previewMigration(): Promise<void> {
    if (!this.ownerId || !this.ownerType || this.loading) {
      return;
    }

    try {
      this.loading = true;
      this.errorMessage = '';
      this.completed = false;

      this.result =
        await this.availabilityService.migrateLegacyAvailability(
          this.ownerType,
          this.ownerId,
          true,
        );

      this.previewCompleted =
        this.result.status === 'previewed';

      if (this.result.status === 'skipped') {
        this.previewCompleted = false;
        this.completed = true;
      }
    } catch (error) {
      console.error(error);

      this.result = null;
      this.previewCompleted = false;
      this.errorMessage = this.getErrorMessage(error);

      this.commanUsesService.showSnackBar(
        'Failed to check old availability.',
      );
    } finally {
      this.loading = false;
    }
  }

  async startMigration(): Promise<void> {
    if (
      !this.previewCompleted ||
      !this.ownerId ||
      !this.ownerType ||
      this.migrating
    ) {
      return;
    }

    const confirmed = window.confirm(
      'Convert today and future availability to the new system?',
    );

    if (!confirmed) {
      return;
    }

    try {
      this.migrating = true;
      this.errorMessage = '';

      this.result =
        await this.availabilityService.migrateLegacyAvailability(
          this.ownerType,
          this.ownerId,
          false,
        );

      this.completed =
        this.result.status === 'migrated' ||
        this.result.status === 'skipped';

      this.previewCompleted = false;

      if (this.result.status === 'migrated') {
        this.commanUsesService.showSnackBar(
          'Availability migrated successfully.',
        );

        this.migrationCompleted.emit();
      } else {
        this.commanUsesService.showSnackBar(
          this.result.reason ||
            'Availability does not require migration.',
        );
      }
    } catch (error) {
      console.error(error);

      this.errorMessage = this.getErrorMessage(error);

      this.commanUsesService.showSnackBar(
        'Availability migration failed.',
      );
    } finally {
      this.migrating = false;
    }
  }

  reset(): void {
    this.loading = false;
    this.migrating = false;
    this.previewCompleted = false;
    this.completed = false;
    this.result = null;
    this.errorMessage = '';
  }

  private getErrorMessage(error: any): string {
    const message =
      error?.error?.message ||
      error?.message ||
      'Something went wrong during migration.';

    return Array.isArray(message)
      ? message.join(', ')
      : String(message);
  }
}